/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';

import { ContactAddressResponse } from '../models/contact-address-response';
import { ContactListResponsePaging } from '../models/contact-list-response-paging';
import { ContactResponse } from '../models/contact-response';
import { ContactSearchResponse } from '../models/contact-search-response';
import { ContactShareCandidateResponse } from '../models/contact-share-candidate-response';
import { createContact } from '../fn/contact/create-contact';
import { CreateContact$Params } from '../fn/contact/create-contact';
import { getContact } from '../fn/contact/get-contact';
import { GetContact$Params } from '../fn/contact/get-contact';
import { getContactAddresses } from '../fn/contact/get-contact-addresses';
import { GetContactAddresses$Params } from '../fn/contact/get-contact-addresses';
import { getContacts } from '../fn/contact/get-contacts';
import { GetContacts$Params } from '../fn/contact/get-contacts';
import { getContactShareCandidates } from '../fn/contact/get-contact-share-candidates';
import { GetContactShareCandidates$Params } from '../fn/contact/get-contact-share-candidates';
import { getContactsSearch } from '../fn/contact/get-contacts-search';
import { GetContactsSearch$Params } from '../fn/contact/get-contacts-search';
import { getContactsXlsx } from '../fn/contact/get-contacts-xlsx';
import { GetContactsXlsx$Params } from '../fn/contact/get-contacts-xlsx';
import { updateContact } from '../fn/contact/update-contact';
import { UpdateContact$Params } from '../fn/contact/update-contact';
import { updateContactShares } from '../fn/contact/update-contact-shares';
import { UpdateContactShares$Params } from '../fn/contact/update-contact-shares';

@Injectable({ providedIn: 'root' })
export class ContactService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getContacts()` */
  static readonly GetContactsPath = '/contacts';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getContacts()` instead.
   *
   * This method doesn't expect any request body.
   */
  getContacts$Response(params: GetContacts$Params, context?: HttpContext): Observable<StrictHttpResponse<ContactListResponsePaging>> {
    return getContacts(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getContacts$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getContacts(params: GetContacts$Params, context?: HttpContext): Observable<ContactListResponsePaging> {
    return this.getContacts$Response(params, context).pipe(
      map((r: StrictHttpResponse<ContactListResponsePaging>): ContactListResponsePaging => r.body)
    );
  }

  /** Path part for operation `createContact()` */
  static readonly CreateContactPath = '/contacts';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createContact()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createContact$Response(params: CreateContact$Params, context?: HttpContext): Observable<StrictHttpResponse<number>> {
    return createContact(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createContact$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createContact(params: CreateContact$Params, context?: HttpContext): Observable<number> {
    return this.createContact$Response(params, context).pipe(
      map((r: StrictHttpResponse<number>): number => r.body)
    );
  }

  /** Path part for operation `getContact()` */
  static readonly GetContactPath = '/contacts/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getContact()` instead.
   *
   * This method doesn't expect any request body.
   */
  getContact$Response(params: GetContact$Params, context?: HttpContext): Observable<StrictHttpResponse<ContactResponse>> {
    return getContact(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getContact$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getContact(params: GetContact$Params, context?: HttpContext): Observable<ContactResponse> {
    return this.getContact$Response(params, context).pipe(
      map((r: StrictHttpResponse<ContactResponse>): ContactResponse => r.body)
    );
  }

  /** Path part for operation `updateContact()` */
  static readonly UpdateContactPath = '/contacts/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateContact()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateContact$Response(params: UpdateContact$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return updateContact(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateContact$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateContact(params: UpdateContact$Params, context?: HttpContext): Observable<void> {
    return this.updateContact$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `getContactAddresses()` */
  static readonly GetContactAddressesPath = '/contacts/{id}/addresses';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getContactAddresses()` instead.
   *
   * This method doesn't expect any request body.
   */
  getContactAddresses$Response(params: GetContactAddresses$Params, context?: HttpContext): Observable<StrictHttpResponse<Array<ContactAddressResponse>>> {
    return getContactAddresses(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getContactAddresses$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getContactAddresses(params: GetContactAddresses$Params, context?: HttpContext): Observable<Array<ContactAddressResponse>> {
    return this.getContactAddresses$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<ContactAddressResponse>>): Array<ContactAddressResponse> => r.body)
    );
  }

  /** Path part for operation `getContactShareCandidates()` */
  static readonly GetContactShareCandidatesPath = '/contacts/{id}/share-candidates';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getContactShareCandidates()` instead.
   *
   * This method doesn't expect any request body.
   */
  getContactShareCandidates$Response(params: GetContactShareCandidates$Params, context?: HttpContext): Observable<StrictHttpResponse<Array<ContactShareCandidateResponse>>> {
    return getContactShareCandidates(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getContactShareCandidates$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getContactShareCandidates(params: GetContactShareCandidates$Params, context?: HttpContext): Observable<Array<ContactShareCandidateResponse>> {
    return this.getContactShareCandidates$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<ContactShareCandidateResponse>>): Array<ContactShareCandidateResponse> => r.body)
    );
  }

  /** Path part for operation `getContactsSearch()` */
  static readonly GetContactsSearchPath = '/contacts/search';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getContactsSearch()` instead.
   *
   * This method doesn't expect any request body.
   */
  getContactsSearch$Response(params: GetContactsSearch$Params, context?: HttpContext): Observable<StrictHttpResponse<Array<ContactSearchResponse>>> {
    return getContactsSearch(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getContactsSearch$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getContactsSearch(params: GetContactsSearch$Params, context?: HttpContext): Observable<Array<ContactSearchResponse>> {
    return this.getContactsSearch$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<ContactSearchResponse>>): Array<ContactSearchResponse> => r.body)
    );
  }

  /** Path part for operation `getContactsXlsx()` */
  static readonly GetContactsXlsxPath = '/contacts/xlsx';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getContactsXlsx()` instead.
   *
   * This method doesn't expect any request body.
   */
  getContactsXlsx$Response(params: GetContactsXlsx$Params, context?: HttpContext): Observable<StrictHttpResponse<Blob>> {
    return getContactsXlsx(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getContactsXlsx$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getContactsXlsx(params: GetContactsXlsx$Params, context?: HttpContext): Observable<Blob> {
    return this.getContactsXlsx$Response(params, context).pipe(
      map((r: StrictHttpResponse<Blob>): Blob => r.body)
    );
  }

  /** Path part for operation `updateContactShares()` */
  static readonly UpdateContactSharesPath = '/contacts/{id}/shares';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateContactShares()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateContactShares$Response(params: UpdateContactShares$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return updateContactShares(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateContactShares$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateContactShares(params: UpdateContactShares$Params, context?: HttpContext): Observable<void> {
    return this.updateContactShares$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

}
